import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

// Extracted password-with-eye-toggle block (#7). Was duplicated verbatim
// across Login / Signup / ForgotPassword (3×, sometimes for new
// passwords / confirm passwords). Each instance manages its own
// show/hide state — no parent plumbing required.

interface Props {
  value: string
  onChange: (next: string) => void
  /** Label text above the field. Default "Password". */
  label?: string
  /** Placeholder inside the field. Default mirrors the label. */
  placeholder?: string
  /** `autoComplete` — "current-password" on login, "new-password" on
   *  signup/forgot. Defaults to "current-password". */
  autoComplete?: string
  /** Disable while a network call is in flight. */
  disabled?: boolean
  /** Hide the label entirely (e.g. when the field is one of two stacked
   *  password fields and the label sits on its sibling). Default false. */
  hideLabel?: boolean
  /** Trim margin-top from the wrapper — handy when the input is the
   *  first child in a card. Default false. */
  topless?: boolean
  inputId?: string
}

export function PasswordInput({
  value, onChange, label = 'Password', placeholder, autoComplete = 'current-password',
  disabled, hideLabel = false, topless = false, inputId,
}: Props) {
  const [show, setShow] = useState(false)
  return (
    <div>
      {!hideLabel && (
        <label htmlFor={inputId} className={`block text-navy text-xs font-semibold mb-1 ${topless ? '' : 'mt-4'}`}>
          {label}
        </label>
      )}
      <div className="flex items-stretch border-2 border-border rounded-lg overflow-hidden">
        <input
          id={inputId}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder={placeholder ?? label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="flex-1 px-3 py-3 text-navy text-sm outline-none disabled:opacity-60 disabled:bg-chatBg"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          disabled={disabled}
          className="px-3 text-gray-500 disabled:opacity-40"
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  )
}
